import router from '@/router';
import { ServicesContainer } from '@/services';
import { AnalyticsEventType, WebPageDetails } from '@/services/analytics/platforms/adobe-analytics/models';
import { AppState } from '@/store';
import { Store } from 'vuex';
import { AdobePageLoadEvent } from './adobe-page-load';
import { ProductListItem } from '../classes/product-list-item';
import moment from 'moment';
import { CheckoutModuleState } from '@/store/checkout';
import { CheckoutEvent } from '@/services/analytics/events';

export class AdobeCheckoutEvent extends AdobePageLoadEvent {
  eventType? : string;
  constructor(event: CheckoutEvent, store: Store<AppState>, services: ServicesContainer) {
    super(store, services);

    Object.assign(this.web.webPageDetails, this.createCheckoutDetails(store.state.ui.countryProfile.countryCode, event.eventType || ''));

    switch (event.eventType) {
      case 'one-time-payment':
        this.web.webPageDetails.subPageName = 'one-time-payment';
        break;
      case 'add-credit-card':
        this.web.webPageDetails.subPageName = 'add-credit-card';
        break;
      case 'add-gift-card':
        this.web.webPageDetails.subPageName = 'add-gift-card';
        break;
      case 'review-order':
        this.web.webPageDetails.subPageName = 'review-order';
        this.commerce = {...this.commerce, orderReview : { value: 1 }};
        this.eventType = 'web.webpagedetails.pageViews';
        break;
      case 'cancel-order-review':
        // Here we combine link click, webpage details, etc with scCheckout event;
        const localTime = moment();
        this.commerce = this.getCommerceReviewModel() ;
        // Format local time after using it create min pickup times.
        // Round down to the nearest 30 min for localTime
        // 18:00 = 18:00:00-18:29:59 
        // 18:30 = 18:30:00-18:59:59
        this.commerce.localTime = localTime.set({minute: localTime.minutes() < 30 ? 0 : 30}).format('HH:mm');
        this.web.webInteraction = {
          name: 'CANCEL ORDER',
          linkClicks: { value: 1 },
          type: 'other',
        };
        //fallthrough
      default:
        // Here we combine webpage details, etc with scCheckout event;
        this.event = AnalyticsEventType.Checkout;
        this.web.webPageDetails.page = 'home';
        this.web.webPageDetails.pageViews = { value: 1 };
        this.commerce = { ...this.commerce, checkouts: { value: 1 }, checkoutFunnelInteractions: { value: 1 }, step1Checkouts: { value: 1 }, selectionLocation: { value: 1 }, funnelName: 'Checkout'};
        const order = services.order.getCurrentOrder();
        // If user lands on checkout page and there are no meals in bag, send empty array
        this.productListItems = ProductListItem.getProductListItems(order?.order.meals || [], store, services.order.isDelivery());
        this.commerce.pickupDeliveryDefaultMethod = order?.order.delivery ? 'Delivery' : 'Pickup';
        // SPUT delivery/pickup times
        this.setDeliveryOrPickupTimes(services, store.state.checkout);

        if (store.state.order.upsellMeal) {
          this.commerce.upsellCheckout = { value: 1 };
        }

        if (store.state.checkout.pickup.alternatePickupLocations.alternatePickupRestaurants.length > 0) {
          this.commerce.alternateLocationImpression = { value: 1 }; 
        }
    }

    if (this.web.webPageDetails.subPageName) {
      this.web.webPageDetails.subPageViews = { value: 1 };
    }
  }

  private getCommerceReviewModel() {
    return { ...this.commerce, orderNotSubmitted: { value: 1 } };
  }

  createCheckoutDetails(countryCode: string, eventType: string): WebPageDetails {
    const siteSection = 'order';
    const pageType = router.currentRoute.name;
    let details = {
      pageType,
      siteSection,
      pageViews: {value: 1},
      subSection1: 'checkout',
      name: `${siteSection}:${pageType}:${eventType ? eventType : 'home'}`,
    } as WebPageDetails;

    return details;
  }

  private setDeliveryOrPickupTimes(services: ServicesContainer, checkoutState: CheckoutModuleState): void {

    const localTime = moment();

    if (services.order.isDelivery()) {
      const driverPickupTime = moment(checkoutState.delivery.estimate?.pickupTime).diff(localTime, 'minutes');
      
      // Only add key/value pairs to event if value is valid
      if(driverPickupTime > 0) {
        this.commerce.driverPickupTime = driverPickupTime < 10 ? '0' + driverPickupTime : driverPickupTime.toString();
      }
      if (checkoutState.delivery.estimate?.minDeliveryTime) {
        this.commerce.minDeliveryTime = checkoutState.delivery.estimate?.minDeliveryTime.toString();
      }
      if (checkoutState.delivery.estimate?.maxDeliveryTime) {
        this.commerce.maxDeliveryTime = checkoutState.delivery.estimate?.maxDeliveryTime.toString();
      }

    } else {
      const minPickupTime = moment(checkoutState.pickup.availablePickupTimes[0]).diff(localTime, 'minutes');

      // Only add key/value pair to event if value is valid
      if (minPickupTime > 0) {
        this.commerce.minPickupTime = minPickupTime < 10 ? '0' + minPickupTime : minPickupTime.toString();
      }
    }

    // Format local time after using it create min pickup times.
    // Round down to the nearest 30 min for localTime
    // 18:00 = 18:00:00-18:29:59 
    // 18:30 = 18:30:00-18:59:59
    this.commerce.localTime = localTime.set({minute: localTime.minutes() < 30 ? 0 : 30}).format('HH:mm');
  }
}
